import { useQuery } from '@apollo/client';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { VerticalCardList } from '../../components/CardList';
import InfoOverlay from '../../components/InfoOverlay';
import LeadAnalyst from '../../components/LeadAnalyst';
import MapInstance from '../../components/Map';
import { TOTAL_PRODUCTION_HEAT_MAP } from '../../components/Map/Settings/constants';
import { MAPBOX_LAYERS } from '../../components/Map/Utils/constants';
import SummaryList from '../../components/SummaryList';
import { analytics, dimensions } from '../../utils/analytics';
import { getUserId } from '../../utils/auth';
import { ANALYTICS_EVENT_ACTIONS, GLOBAL_SEARCH_ACTION_DETAIL } from '../../utils/constants';
import {
  getFulltextResultParametersFromSearch,
  useGlobalSearchParameters,
} from '../../utils/globalSearch';
import { formatDateShort, getLatLongFromPoint, roundAndFormat } from '../../utils/helpers';
import { ProductionLineGraphProvider } from '../PortfolioAnalysis/PortfolioGraphing/LineGraphs';
import { CONTEXT, GA_PAGE, LAYERS, mapSettings, sortTypes, summaryItems } from './constants';
import DownloadButton from './downloadButton';
import Filters from './Filters';
import Links from './Links';
import { getNewPortfolio } from './Links/utils';
import { COUNTRIES_QUERY } from './operations';
import ProductionLineGraph from './productionLineGraph';

const defaultFilters = {
  regions: [],
  countries: [],
};

const useStyles = makeStyles(theme => ({
  root: {
    ...theme.sizes.fullPage,
    width: '100%',
    flexWrap: 'nowrap',
    overflow: 'hidden',
  },
  infoOverlayRoot: {
    width: 'calc(100% - 305px) !important',
    left: '305px',
    borderTopLeftRadius: '5px',
    borderTopRightRadius: '5px',
  },
  infoOverlayContent: {
    height: '100%',
  },
  filters: {
    backgroundColor: 'transparent',
    padding: '10px',
    margin: 0,
  },
  cardList: {
    ...theme.palette.icedBackground,
    ...theme.mapPanel,
    padding: '0 10px 10px 10px',
  },
  overViewItem: {
    position: 'relative',
    padding: '0 10px',
    height: '100%',
  },
  scroll: {
    ...theme.palette.altScrollbar,
    overflow: 'auto',
  },
  divider: {
    ...theme.palette.transparentBackgroundAccent,
    width: '100%',
    height: '2px',
    margin: '20px 0',
  },
}));

const getDefaultFilters = () => {
  const filters = JSON.parse(sessionStorage.getItem('countryOverviewFilters'));
  if (
    filters &&
    Object.keys(defaultFilters).every(filter => Object.keys(filters).includes(filter))
  ) {
    return filters;
  }
  return defaultFilters;
};

let overviewShouldOpen = false;

const MAP_ICON_SPACE_25 = 25;

const CountryOverviews = () => {
  const classes = useStyles();

  const [map, setMap] = useState(null);
  const [selectedCountry, setCountry] = useState(null);
  const [clickedCountry, setClickedCountry] = useState(null);
  const [filters, setFilterState] = useState(getDefaultFilters());
  const [overViewOpen, setOverviewOpen] = useState(false);
  const [sortType, setSortType] = useState(sortTypes[0]);
  const [filtersHeight, setFiltersHeight] = useState(0);

  const cardListStyle = {
    // position from top of page = appbar height + filters height
    top: `${filtersHeight}px`,
    height: `calc(100% - ${20 + MAP_ICON_SPACE_25 + filtersHeight}px)`,
  };
  const overlayStyle = {
    top: `${10 + filtersHeight}px`,
    height: `calc(100% - ${10 + filtersHeight}px)`,
  };
  const filtersRef = useRef(null);

  const { loading, data } = useQuery(COUNTRIES_QUERY, {
    variables: {
      filters,
      sortBy: sortType.value,
    },
  });

  const findCountry = isoCode => {
    return data?.countryStatistics?.find(c => c.isoCode === isoCode);
  };

  // Set label and hover data so the map can access the info in callbacks
  const setHoverData = (data, label) => {
    map.legendlabel = label;
    map.hoverdata = data;
  };

  // Hover event callback
  const mousemove = (e, map) => {
    const displayProperties = ['properties', 'layer', 'sourceLayer'];
    const displayFeatures = map.getFeaturesFromMap(e, displayProperties);

    // Find the feature that matches one of the country source layers
    const displayFeature = displayFeatures.find(
      feature =>
        feature.sourceLayer === MAPBOX_LAYERS.countryBoundaries ||
        feature.sourceLayer === MAPBOX_LAYERS.customRegions,
    );

    // Set the hover html
    if (displayFeature) {
      const { country_name, iso_3166_1_alpha_3: isoCode, name_en } = displayFeature.properties;
      const country = country_name || name_en;
      const value = roundAndFormat(map.hoverdata[isoCode], 2);
      if (value) {
        const infoHtml = `
          <div>
            <div>${map.legendlabel}</div>
            <p>
              <span>${country}:</span> ${value}
            </p>
          </div>
        `;
        const coordinates = e.lngLat;
        map.setPopup(infoHtml, coordinates);
        return;
      }
    }
    // If no feature is found or the value does not exist, remove the popup
    map.infoPopup.remove();
  };

  const zoomend = () => {
    if (overviewShouldOpen) {
      setOverviewOpen(true);
      overviewShouldOpen = false;
    }
  };

  const zoomToCountry = country => {
    overviewShouldOpen = true;
    const { geom, zoom } = country;
    const center = getLatLongFromPoint(geom);
    const { lng, lat } = map.getCenter();
    const currentZoom = map.getZoom();
    if (
      parseFloat(center[0]).toFixed(6) === lng.toFixed(6) &&
      parseFloat(center[1]).toFixed(6) === lat.toFixed(6) &&
      zoom === currentZoom
    ) {
      zoomend();
    } else {
      map.flyTo({
        center,
        zoom,
      });
    }
  };

  const handleCardClick = country => {
    setCountry(country);
    zoomToCountry(country);
  };

  const setFilters = newFilters => {
    if (newFilters.regions.length === 1 && filters.regions.length === 0) {
      const region = newFilters.regions[0];
      map.flyTo({
        center: region.coord,
        zoom: region.zoom,
      });
    } else if (newFilters.countries.length > filters.countries.length) {
      const lastIdx = newFilters.countries.length - 1;
      const lastCountryIsoCode = newFilters.countries[lastIdx].isoCode;
      const newSelectedCountry = findCountry(lastCountryIsoCode);
      handleCardClick(newSelectedCountry);
    }
    sessionStorage.setItem('countryOverviewFilters', JSON.stringify(newFilters));
    gtag('event', ANALYTICS_EVENT_ACTIONS.event, {
      event_category: GA_PAGE,
      event_action: ANALYTICS_EVENT_ACTIONS.filter,
      event_label: analytics(newFilters),
      userId: getUserId(),
      ...dimensions(newFilters, CONTEXT),
    });

    setFilterState(newFilters);
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    setOverviewOpen(false);
    map.resetZoom();
  };

  const removeLayerCallback = () => {
    setCountry(null);
    setClickedCountry(null);
  };

  const getDateText = item => item?.countryStatistics?.reportUpdatedAt || '-';

  // biome-ignore lint/correctness/useExhaustiveDependencies: afraid to add missing deps and break something
  useEffect(() => {
    setFiltersHeight(filtersRef.current.offsetHeight);
    setOverviewOpen(false);
  }, [filters]);

  // handle user clicks country
  // biome-ignore lint/correctness/useExhaustiveDependencies: afraid to add missing deps and break something
  useEffect(() => {
    // match selected country with clicked country, highlight, and simulate card click
    const newCountry = findCountry(clickedCountry?.layerCountry);
    if (newCountry) {
      // if user clicks on a country that has country and custom region boundaries, need to swap to correct boundary
      // ex. user clicks on USA but needs to highlight GoM custom boundary
      if (
        (newCountry.customRegion && clickedCountry.sourceLayer === 'countries') ||
        (!newCountry.customRegion && clickedCountry.sourceLayer === 'custom_regions')
      ) {
        setClickedCountry({
          ...clickedCountry,
          sourceLayer: clickedCountry.sourceLayer === 'countries' ? 'custom_regions' : 'countries',
        });
      } else {
        handleCardClick(newCountry);
      }
    }
  }, [clickedCountry]);

  // match clicked country with selected country and highlight
  // biome-ignore lint/correctness/useExhaustiveDependencies: afraid to add missing deps and break something
  useEffect(() => {
    if (clickedCountry?.layerCountry !== selectedCountry?.isoCode) {
      const newClickedCountry = {
        properties: {
          iso_3166_1_alpha_3: selectedCountry.isoCode,
        },
        sourceLayer: selectedCountry.customRegion ? 'custom_regions' : 'countries',
        layerCountry: selectedCountry.isoCode,
      };
      setClickedCountry(newClickedCountry);
    }
  }, [selectedCountry]);

  // set portfolio object for production graph
  const portfolios = useMemo(() => {
    return [getNewPortfolio(selectedCountry)];
  }, [selectedCountry]);

  // biome-ignore lint/correctness/noNestedComponentDefinitions: this needs refactoring
  const HeaderContainer = () => (
    <>
      <Grid style={{ display: 'flex' }} size={2}>
        <DownloadButton country={selectedCountry} />
      </Grid>
      <Grid size={4}>
        <LeadAnalyst country={selectedCountry} horizontal />
      </Grid>
    </>
  );

  // biome-ignore lint/correctness/noNestedComponentDefinitions: this needs refactoring
  const FiltersContainer = () => (
    <Grid
      ref={filtersRef}
      container
      className={clsx({
        [classes.filters]: true,
      })}
    >
      <Filters filters={filters} setFilters={setFilters} clearFilters={clearFilters} />
    </Grid>
  );

  // biome-ignore lint/correctness/noNestedComponentDefinitions: this needs refactoring
  const CardListContainer = () => (
    <Grid className={clsx({ [classes.cardList]: true })} style={cardListStyle}>
      <VerticalCardList
        items={data ? data.countryStatistics : []}
        getDateText={getDateText}
        setSortType={setSortType}
        sortType={sortType}
        sortTypes={sortTypes}
        title='COUNTRY / AREA'
        loading={loading}
        onCardClick={handleCardClick}
        selectedItem={selectedCountry}
        rootInlineStyle={cardListStyle}
      />
    </Grid>
  );

  const layerClickCallback = clickedCountry => {
    setClickedCountry(clickedCountry);
  };

  // Global Search block
  // Getting the query parameters from the URL
  const searchParams = useGlobalSearchParameters();
  const gloSearchParams = getFulltextResultParametersFromSearch(searchParams);

  // Using hooking the zooming to the selected country
  // biome-ignore lint/correctness/useExhaustiveDependencies: afraid to add missing deps and break something
  useEffect(() => {
    if (map && gloSearchParams) {
      switch (gloSearchParams.action) {
        case GLOBAL_SEARCH_ACTION_DETAIL: {
          const country = findCountry(gloSearchParams.isoCode);
          if (country) {
            handleCardClick(country);
          }
          return;
        }
      }
      console.log('Unsupported action type', gloSearchParams);
    }
  }, [gloSearchParams]);

  return (
    <Grid className={classes.root} container direction='column'>
      <FiltersContainer />
      <MapInstance
        map={map}
        callbacks={{ mousemove, zoomend }}
        enableInfoPopup
        layerClickCallback={layerClickCallback}
        setMap={setMap}
        settingsProps={{
          ...mapSettings,
          layerProps: {
            filters,
            defaultLayer: TOTAL_PRODUCTION_HEAT_MAP.value,
            setData: setHoverData,
          },
          top: filtersHeight + 64,
        }}
        countryClickProps={{
          enabled: true,
          filters,
          selectedCountry,
        }}
        removeLayerCallback={removeLayerCallback}
        layers={LAYERS}
      />
      <CardListContainer />
      <InfoOverlay
        isOpen={overViewOpen}
        onClose={() => setOverviewOpen(false)}
        header={selectedCountry?.displayName}
        subHeader={`updated:\n${formatDateShort(getDateText(selectedCountry))}`}
        headerAdornment={<HeaderContainer />}
        inlineStyles={overlayStyle}
        styles={classes}
      >
        <Grid className={clsx(classes.overViewItem, classes.scroll)} size={7}>
          <SummaryList data={selectedCountry?.countryStatistics} items={summaryItems} />
          <Divider className={classes.divider} />
          <ProductionLineGraphProvider portfolios={[...portfolios]}>
            <ProductionLineGraph portfolio={portfolios[0]} />
          </ProductionLineGraphProvider>
        </Grid>
        <Grid className={clsx(classes.overViewItem, classes.scroll)} size={5}>
          <Links country={selectedCountry} />
        </Grid>
      </InfoOverlay>
    </Grid>
  );
};

export default CountryOverviews;
